// Modal informativo "esta función se usa desde la computadora". Se muestra
// cuando un usuario HABILITADO abre desde el celular una feature que solo corre
// en pantallas grandes (p. ej. el Estudio de Video pro): la card se ve en mobile
// para que descubra la feature, pero al tocarla se le avisa que la use en desktop.
//
// Mismo lenguaje visual que `FeatureLockedModal` (portaleado a body, tarjeta
// centrada, ícono en círculo), pero es solo informativo: un único botón que
// cierra, sin CTA a planes (el usuario YA tiene el plan).

import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Monitor } from 'lucide-react';

export interface DesktopOnlyModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Explicación (texto o nodos). */
  message?: React.ReactNode;
  /** Texto del botón. Default "Entendido". */
  ctaLabel?: string;
}

export const DesktopOnlyModal: React.FC<DesktopOnlyModalProps> = ({
  open,
  onClose,
  title = 'Entrá desde la computadora',
  message = 'El Estudio de Video funciona en pantallas grandes. Ya lo tenés disponible: abrilo desde tu computadora para editar tus videos.',
  ctaLabel = 'Entendido',
}) => {
  const titleId = useId();
  const descId = useId();

  // Cerrar con Escape — comportamiento estándar de un diálogo modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  // Portaleado a document.body (igual que FeatureLockedModal): así aparece sobre
  // el shell del App y no queda atrapado bajo la barra de navegación inferior.
  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4"
      >
        <div className="p-5">
          <div className="flex justify-center mb-4">
            <span className="w-12 h-12 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Monitor className="w-6 h-6 text-brand-primary" />
            </span>
          </div>
          <h3
            id={titleId}
            className="text-[17px] font-semibold text-brand-black/90 mb-2 text-center"
          >
            {title}
          </h3>
          <p id={descId} className="text-[15px] text-neutral-600 mb-6 text-center">
            {message}
          </p>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center text-center h-10 px-6 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DesktopOnlyModal;
